import type { MedplumClient } from '@medplum/core';
import { config } from '../config';
import { getMedplumRecaptchaFields, isHostedMedplumBaseUrl } from './medplumRecaptcha';
import { UserFacingError } from './userFacingError';

export interface ActiveLoginSession {
  id?: string;
  loginId?: string;
  userAgent?: string;
  remoteAddress?: string;
  remoteHost?: string;
  lastSeen?: string;
  createdTime?: string;
  authMethod?: string;
  current?: boolean;
}

interface MedplumAuthMeSession {
  id?: string;
  lastUpdated?: string;
  authMethod?: string;
  remoteAddress?: string;
  browser?: string;
  os?: string;
}

export interface AuthMeSnapshot {
  /** Medplum User login email from /auth/me (matches reset-password lookup). */
  loginEmail?: string;
  sessions: ActiveLoginSession[];
}

function getOperationOutcomeMessage(body: unknown): string | undefined {
  const issue = (body as { issue?: Array<{ details?: { text?: string }; diagnostics?: string }> })?.issue?.[0];
  return issue?.details?.text ?? issue?.diagnostics;
}

function formatMedplumSessionUserAgent(session: MedplumAuthMeSession): string | undefined {
  const { browser, os } = session;
  if (browser && os) return `${browser} on ${os}`;
  return browser ?? os;
}

function parseActiveLoginSessions(result: unknown): ActiveLoginSession[] {
  const securitySessions = (result as { security?: { sessions?: MedplumAuthMeSession[] } }).security?.sessions;
  if (Array.isArray(securitySessions)) {
    return securitySessions.map((session) => ({
      id: session.id,
      loginId: session.id,
      userAgent: formatMedplumSessionUserAgent(session),
      remoteAddress: session.remoteAddress,
      lastSeen: session.lastUpdated,
      authMethod: session.authMethod,
    }));
  }

  // Defensive fallbacks for older/alternate auth/me shapes.
  const candidate =
    (result as { logins?: ActiveLoginSession[] }).logins ??
    (result as { sessions?: ActiveLoginSession[] }).sessions ??
    (result as { activeSessions?: ActiveLoginSession[] }).activeSessions ??
    [];

  return Array.isArray(candidate) ? candidate : [];
}

export function latestSessionLastSeen(sessions: ActiveLoginSession[]): string | undefined {
  const times = sessions
    .map((session) => session.lastSeen?.trim())
    .filter((value): value is string => Boolean(value) && value !== '—');
  if (times.length === 0) return undefined;
  return times.reduce((best, value) => (value > best ? value : best));
}

export async function fetchAuthMeSnapshot(medplum: MedplumClient): Promise<AuthMeSnapshot> {
  const result = await medplum.get('auth/me');
  const rawLoginEmail = (result as { user?: { email?: string } }).user?.email;
  const loginEmail = rawLoginEmail?.trim().toLowerCase() || undefined;

  return {
    loginEmail,
    sessions: parseActiveLoginSessions(result),
  };
}

export async function fetchActiveLoginSessions(medplum: MedplumClient): Promise<ActiveLoginSession[]> {
  const snapshot = await fetchAuthMeSnapshot(medplum);
  return snapshot.sessions;
}

function collectLoginIds(sessions: ActiveLoginSession[]): string[] {
  return [
    ...new Set(
      sessions
        .map((session) => session.loginId ?? session.id)
        .filter((loginId): loginId is string => Boolean(loginId)),
    ),
  ];
}

function parseJwtPayload(accessToken: string): Record<string, unknown> | undefined {
  const parts = accessToken.split('.');
  if (parts.length !== 3) {
    return undefined;
  }

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

function getCurrentLoginId(medplum: MedplumClient): string | undefined {
  const accessToken = medplum.getAccessToken();
  if (!accessToken) {
    return undefined;
  }

  const loginId = parseJwtPayload(accessToken)?.login_id;
  return typeof loginId === 'string' ? loginId : undefined;
}

export async function revokeLoginSession(medplum: MedplumClient, loginId: string): Promise<void> {
  await medplum.post('auth/revoke', { loginId });
}

/** Revoke other active sessions, then sign out the current device. */
export async function logoutAllDevices(medplum: MedplumClient): Promise<void> {
  const snapshot = await fetchAuthMeSnapshot(medplum);
  const currentLoginId = getCurrentLoginId(medplum);
  const otherLoginIds = collectLoginIds(snapshot.sessions).filter((loginId) => loginId !== currentLoginId);

  await Promise.allSettled(otherLoginIds.map((loginId) => revokeLoginSession(medplum, loginId)));

  try {
    await medplum.signOut();
  } catch {
    // If the current session was already revoked server-side, still clear local auth state.
    medplum.clear();
  }
}

export async function triggerPasswordResetEmail(medplum: MedplumClient, email: string): Promise<void> {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) {
    throw new UserFacingError('No email address is available for this account.');
  }

  // Match ForgotPasswordPage + PRD: sendEmail:false lets Medplum create UserSecurityRequest;
  // email-sender bot Subscription sends the branded reset email.
  const body: Record<string, unknown> = { email: normalizedEmail, sendEmail: false };

  const baseUrl = medplum.getBaseUrl();
  const hosted = isHostedMedplumBaseUrl(baseUrl);
  const projectId = config.medplum.projectId;
  if (projectId) {
    body.projectId = projectId;
  } else if (hosted) {
    throw new Error('Password reset is not configured for this environment (missing project ID).');
  }

  const recaptcha = await getMedplumRecaptchaFields('reset_password');
  if (recaptcha) {
    body.recaptchaToken = recaptcha.recaptchaToken;
    body.recaptchaSiteKey = recaptcha.recaptchaSiteKey;
  } else if (hosted) {
    throw new Error('Password reset is not configured for this environment (missing reCAPTCHA site key).');
  }

  const res = await fetch(`${baseUrl}auth/resetpassword`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const responseBody = await res.json().catch(() => undefined);
    throw new Error(getOperationOutcomeMessage(responseBody) ?? 'Could not send password reset email.');
  }
}
